/**
 * Echo Hunt -- the shared reshape step and the round bullet pool (P7, §6 + §4.5 + §7).
 *
 * ## The shared reshape (§6)
 *
 * Every bullet -- real or fake -- goes through the SAME reshaping call: "rewrite this bullet so it
 * works in these words". Two things follow from doing it to both classes:
 *  1. some unusualness is injected into EVERY bullet, so "an odd object appears" stops being a rule
 *     the user can learn (§6 "accepted cost", §17 learned irrelevance);
 *  2. no bullet reaches the weaver un-reshaped, so the surface style of the two classes cannot diverge
 *     at this step (§2 invariant #3).
 *
 * The `k` words per bullet are drawn from ONE distribution for both classes, by bullet length (§6), so
 * a count rule ("2 odd words means fake") gives no certainty either. `SampleReshapeWordCount` takes the
 * bullet TEXT and nothing else -- there is deliberately no parameter through which a class could be
 * passed, which is what makes the symmetry structural rather than a matter of discipline.
 *
 * REAL bullets are additionally retention-checked (§6, `Prompts/Retention.ts`): a reshape that replaces
 * the event with a different one would ship a bullet labelled REAL that describes something the user
 * never dreamt -- the false-memory risk in §17 -- so it is regenerated (up to 2 more times) and then
 * dropped. Fakes need no such check: there is no source event to drift from.
 *
 * ## The pool (§16.2 P7)
 *
 * `BuildBulletPool` assembles one round's bullets: sample reals with fallowness weighting (§4.5),
 * generate the fakes (P6), reshape everything, run the §7 check on everything, then shuffle and assign
 * RANDOM slot ids. Slot ids are assigned after the shuffle and are never derived from the source id or
 * the class (§16.0 "label hygiene") -- otherwise the order of the story would leak the labels.
 *
 * PURE module: no React, no `store`, no DB imports (see README.md). The LLM is passed in as an
 * `LLMBackend`, so tests use `MockLLMBackend`.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §4.5, §6, §7, §2 (#3), §16.2 P7.
 */
import {CallLLM, LLMBackend, LLMCallCounter} from "./LLM.js";
import {CheckBullet, CheckBulletOptions, CountWords} from "./Normalize.js";
import {
	Reshape_JSONSchema,
	Reshape_SystemPrompt,
	Reshape_UserPrompt,
} from "./Prompts/Reshape.js";
import {
	Retention_JSONSchema,
	Retention_SystemPrompt,
	Retention_UserPrompt,
} from "./Prompts/Retention.js";
import {
	FakeGenerationDistributions,
	GenerateFakes,
	RejectionSamplingOptions,
} from "./FakeGenerator.js";
import {SampleSeedWords} from "./Wordlist.js";
import {GeneratedBullet, RoundSlot} from "./Types.js";
import {FindPhraseLeaks, TokenizeForOverlap} from "./PhraseOverlap.js";

// phrase leaks (2026-10-21)
// ==========

/** What to do when EVERY attempt still copies a 5+-word phrase from the original (`PhraseOverlap.ts`).
 *  - "keep": use the least-leaky attempt (fewest copied words), still subject to the §7 check and, for a real
 *    bullet, the retention check. The leak stays visible in the judge-loop inspector.
 *  - "drop": treat it like any other failed bullet. NOTE: a dropped bullet is NOT replaced -- the round just
 *    comes out one bullet smaller (and, for a real, with a lower real share than requested).
 * Manual toggle; the user has not decided yet, so the default is the less disruptive one. */
export const RESHAPE_PERSISTENT_LEAK_POLICY: "keep"|"drop" = "keep";

function CountLeakedWords(leaks: string[]): number {
	return leaks.reduce((sum, leak)=>sum + TokenizeForOverlap(leak).length, 0);
}

// the shared `k` distribution (§6)
// ==========

/** Bullets at or below this word count get a single seeded word. A short recollection has no room for
 * two peripheral details without turning into a different event. */
export const RESHAPE_SHORT_BULLET_WORDS = 14;

/** Probability that a longer bullet gets 2 words rather than 1. Below 1 on purpose: the §6 requirement
 * is that the count VARIES within each class, so no "2 words = fake" (or "= real") rule can form. */
export const RESHAPE_TWO_WORD_PROBABILITY = .6;

/** How many seeded words one bullet gets (§6: "1-2, by bullet length").
 *
 * Deliberately takes the bullet TEXT and nothing else: this is the function that makes the two classes
 * symmetric, and it can only stay symmetric while it is impossible to tell it which class it is serving.
 * Same text + same rng state => same count, for a real bullet and a fake alike. */
export function SampleReshapeWordCount(bulletText: string, rng: () => number): number {
	const words = CountWords(bulletText);
	if (words <= RESHAPE_SHORT_BULLET_WORDS) return 1;
	return rng() < RESHAPE_TWO_WORD_PROBABILITY ? 2 : 1;
}

// reshaping one bullet
// ==========

export type ReshapeOptions = {
	/** the words that must be worked in (§6); empty in an unseeded round, which still retells the bullet */
	seedWords: string[];
	/** texts the result must not copy 5+-word runs from (`PhraseOverlap.ts`). The input bullet itself is always
	 * included; a real bullet's caller adds its source dream's raw journal text. */
	leakReferences?: string[]|n;
	/** phrases to quote back on the FIRST call (a retry carried over from an outer loop) */
	avoidPhrases?: string[]|n;
	/** default `RESHAPE_PERSISTENT_LEAK_POLICY` */
	leakPolicy?: "keep"|"drop";
	/** terms that must never appear (§4.4) */
	bannedWords?: string[]|n;
	/** cliché phrases to avoid (§5.5) */
	blacklist?: string[]|n;
	/** minimum words per bullet; below this the result is rejected (§4.4 granularity) */
	minWords?: number|n;
	/** model for the `reshape` role; "" lets the backend/settings decide */
	model?: string|n;
	/** per-round tally (§13.1); omitted for one-off/dev calls */
	counter?: LLMCallCounter|n;
	/** attempts before giving up on this bullet. Default 3 (1 call + 2 regenerations, §16.2 P7). */
	maxAttempts?: number;
};

export type ReshapeResult = {
	/** the reshaped bullet, or null when every attempt was rejected */
	text: string|null;
	/** how many model calls this took (1 = clean first time) */
	attempts: number;
	outputTokens: number;
	/** the violations of the LAST rejected candidate, for the dev tool / logs */
	violations: string[];
	/** phrases the RETURNED text still copies from the references (empty = clean). Non-empty only under the
	 * "keep" policy. When `text` is null, these are the last attempt's leaks (for the next retry). */
	phraseLeaks: string[];
	/** when `text` is null because of leaks: the least-leaky candidate, so an outer loop can still fall back
	 * to it under the "keep" policy */
	leakyCandidateText?: string|n;
};

/** Reshapes ONE bullet with the given words (§6), rejecting candidates that break the §7 check.
 *
 * The check runs with the profile's banned words and blacklist but NOT the seed words: unlike the fake
 * generator (where the seeds are a leak), here the seeded words are supposed to appear. */
export async function ReshapeBullet(backend: LLMBackend, bulletText: string, options: ReshapeOptions): Promise<ReshapeResult> {
	const maxAttempts = Math.max(1, options.maxAttempts ?? 3);
	const leakPolicy = options.leakPolicy ?? RESHAPE_PERSISTENT_LEAK_POLICY;
	const checkOptions: CheckBulletOptions = {
		bannedWords: options.bannedWords ?? [],
		blacklist: options.blacklist ?? [],
		minWords: options.minWords ?? undefined,
	};
	const leakReferences = [bulletText, ...(options.leakReferences ?? [])];

	let outputTokens = 0;
	let lastViolations: string[] = [];
	let avoidPhrases: string[] = options.avoidPhrases ?? [];
	/** the least-leaky candidate that otherwise passed, for the "keep" policy */
	let bestLeaky: {text: string, leaks: string[], leakedWords: number}|null = null;

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		const result = await CallLLM(backend, {
			role: "reshape",
			model: options.model ?? null,
			system: Reshape_SystemPrompt(),
			user: Reshape_UserPrompt({bulletText, seedWords: options.seedWords, avoidPhrases}),
			json: Reshape_JSONSchema(),
			counter: options.counter ?? null,
		});
		outputTokens += result.outputTokens;

		const text = typeof (result.parsed as {text?: unknown}|n)?.text == "string"
			? ((result.parsed as {text: string}).text).trim()
			: "";
		if (text.length == 0) {
			lastViolations = ["the reply was empty or not valid JSON"];
			continue;
		}

		const violations = CheckBullet(text, checkOptions);
		if (violations.length > 0) {
			lastViolations = violations.map(a=>`${a.kind}: ${a.message}`);
			continue;
		}

		const leaks = FindPhraseLeaks(text, leakReferences);
		if (leaks.length > 0) {
			lastViolations = [`phraseLeak: copied ${leaks.map(a=>`"${a}"`).join(", ")}`];
			const leakedWords = CountLeakedWords(leaks);
			if (bestLeaky == null || leakedWords < bestLeaky.leakedWords) bestLeaky = {text, leaks, leakedWords};
			// the next attempt is told exactly what to change, rather than hoping it varies
			avoidPhrases = leaks;
			continue;
		}

		return {text, attempts: attempt + 1, outputTokens, violations: [], phraseLeaks: []};
	}

	if (bestLeaky != null && leakPolicy == "keep") {
		return {text: bestLeaky.text, attempts: maxAttempts, outputTokens, violations: [], phraseLeaks: bestLeaky.leaks};
	}
	return {text: null, attempts: maxAttempts, outputTokens, violations: lastViolations, phraseLeaks: bestLeaky?.leaks ?? [], leakyCandidateText: bestLeaky?.text ?? null};
}

// retention (real bullets only, §6)
// ==========

export type RetentionResult = {
	/** does the reshaped text still describe the source event? */
	same: boolean;
	/** the model's one-sentence reason, for logs */
	reason: string|null;
	outputTokens: number;
};

/** `CheckRetention(source, reshaped)` (§6): does the reshaped bullet still describe the source event?
 *
 * A failure is NOT a style problem -- it means the reshape replaced the event, which for a REAL bullet
 * would ship a false memory (§17), so the caller regenerates rather than accepting it. */
export async function CheckRetention(backend: LLMBackend, sourceText: string, reshapedText: string, options?: {model?: string|n, counter?: LLMCallCounter|n}): Promise<RetentionResult> {
	const result = await CallLLM(backend, {
		role: "retention",
		model: options?.model ?? null,
		system: Retention_SystemPrompt(),
		user: Retention_UserPrompt({sourceText, reshapedText}),
		json: Retention_JSONSchema(),
		counter: options?.counter ?? null,
		maxAttempts: 2,
	});

	const parsed = result.parsed as {sameEvent?: unknown, reason?: unknown}|n;
	// an unparseable answer is treated as a FAILURE rather than a pass: dropping a bullet costs one
	// round's supply, while accepting an eroded one corrupts what the user is being trained on
	if (typeof parsed?.sameEvent != "boolean") {
		return {same: false, reason: "retention check produced no usable answer", outputTokens: result.outputTokens};
	}

	return {
		same: parsed.sameEvent,
		reason: typeof parsed.reason == "string" ? parsed.reason : null,
		outputTokens: result.outputTokens,
	};
}

// reshape + retention, per bullet
// ==========

export type ReshapeWithRetentionOptions = ReshapeOptions & {
	/** true for journal bullets: they get the retention check; fakes do not (no source to drift from) */
	isReal: boolean;
	/** the source event the retention check compares against (the indexed `selfContainedText`) */
	sourceText: string;
};

export type ReshapeWithRetentionResult = ReshapeResult & {
	/** false when a real bullet's reshape lost the event (fakes are always `true`: nothing to lose) */
	retentionOk: boolean;
	/** why the bullet was dropped, when `text` is null */
	dropReason: "style"|"retention"|"phraseLeak"|null;
	retentionReasons: string[];
};

/** Reshapes one bullet, and -- for REAL bullets -- keeps regenerating until the result still describes
 * the source event, up to `maxAttempts` model calls in total. §16.2 P7: "up to 2 regenerations, then
 * drop the bullet". Dropping is the safe failure: the round is then one bullet smaller, rather than
 * containing a real-labelled bullet about an event that never happened (§17 false memories). */
export async function ReshapeBulletWithRetention(backend: LLMBackend, bulletText: string, options: ReshapeWithRetentionOptions): Promise<ReshapeWithRetentionResult> {
	const maxAttempts = Math.max(1, options.maxAttempts ?? 3);
	const leakPolicy = options.leakPolicy ?? RESHAPE_PERSISTENT_LEAK_POLICY;
	const retentionReasons: string[] = [];

	let outputTokens = 0;
	let lastViolations: string[] = [];
	let sawStylisticReject = false;
	let avoidPhrases: string[] = options.avoidPhrases ?? [];
	/** the least-leaky otherwise-clean candidate, for the "keep" policy (retention-checked only if used) */
	let bestLeaky: {text: string, leaks: string[], leakedWords: number}|null = null;

	for (let attempt = 0; attempt < maxAttempts; attempt++) {
		// ONE reshape call per outer attempt: the outer loop is the retry budget (§16.2 P7 "up to 2
		// regenerations"), so nesting `ReshapeBullet`'s own retries inside it would turn 3 attempts into 9
		// paid calls. A stylistic rejection here simply consumes one outer attempt. The leak policy is forced to
		// "drop" for the inner call so a leak comes back as a rejection this loop can retry.
		const reshaped = await ReshapeBullet(backend, bulletText, {...options, maxAttempts: 1, avoidPhrases, leakPolicy: "drop"});
		outputTokens += reshaped.outputTokens;

		if (reshaped.text == null) {
			lastViolations = reshaped.violations;
			if (reshaped.phraseLeaks.length > 0) {
				// a leak is retried with the phrases quoted back; it is not a stylistic failure
				avoidPhrases = reshaped.phraseLeaks;
				const leakedWords = CountLeakedWords(reshaped.phraseLeaks);
				const candidateText = reshaped.leakyCandidateText ?? null;
				if (candidateText != null && (bestLeaky == null || leakedWords < bestLeaky.leakedWords)) {
					bestLeaky = {text: candidateText, leaks: reshaped.phraseLeaks, leakedWords};
				}
			} else {
				sawStylisticReject = true;
			}
			continue;
		}

		if (!options.isReal) {
			return {
				text: reshaped.text, attempts: attempt + 1, outputTokens, violations: [], phraseLeaks: [],
				retentionOk: true, dropReason: null, retentionReasons,
			};
		}

		const retention = await CheckRetention(backend, options.sourceText, reshaped.text, {model: options.model, counter: options.counter});
		outputTokens += retention.outputTokens;

		if (retention.same) {
			return {
				text: reshaped.text, attempts: attempt + 1, outputTokens, violations: [], phraseLeaks: [],
				retentionOk: true, dropReason: null, retentionReasons,
			};
		}

		retentionReasons.push(retention.reason ?? "the event changed");
	}

	// "keep" policy: fall back to the least-leaky candidate. A real one must still keep its event -- a leaky
	// sentence is a shallow tell, but an eroded one is a false memory (§17), which is worse.
	if (bestLeaky != null && leakPolicy == "keep") {
		let keepOk = true;
		if (options.isReal) {
			const retention = await CheckRetention(backend, options.sourceText, bestLeaky.text, {model: options.model, counter: options.counter});
			outputTokens += retention.outputTokens;
			keepOk = retention.same;
			if (!keepOk) retentionReasons.push(retention.reason ?? "the event changed");
		}
		if (keepOk) {
			return {
				text: bestLeaky.text, attempts: maxAttempts, outputTokens, violations: [], phraseLeaks: bestLeaky.leaks,
				retentionOk: true, dropReason: null, retentionReasons,
			};
		}
	}

	return {
		text: null, attempts: maxAttempts, outputTokens, violations: lastViolations, phraseLeaks: bestLeaky?.leaks ?? [],
		retentionOk: false,
		dropReason: retentionReasons.length > 0 ? "retention" : (sawStylisticReject ? "style" : (bestLeaky != null ? "phraseLeak" : null)),
		retentionReasons,
	};
}

// sampling real bullets (§4.5)
// ==========

/** The minimal corpus row this module needs; `EchoCorpusBullet` is structurally assignable to it. */
export type RealBulletForPool = {
	/** the corpus row id (kept on the slot as `sourceId`) */
	id: string;
	/** the indexed, house-style text (§4.2) -- what actually gets reshaped */
	text: string;
	/** the parent dream. Same-night dreams count as ONE family (§4.5), so the Live layer passes a
	 * same-night-stable id here rather than the raw dream id. */
	familyId: string;
	/** how many rounds have shown this bullet (§4.5) */
	exposureRoundCount?: number|n;
	/** how many times the user actually saw it at reveal (§4.5) */
	exposureRevealCount?: number|n;
	/** the user's OWN wording the reshape must not copy 5+-word runs from: this bullet's raw journal text and
	 * the rest of its source dream (the index pass may have pulled in wording from neighbouring bullets).
	 * `text` itself is always checked too. Never sent to a model -- only the deterministic check reads it. */
	leakReferences?: string[]|n;
};

/** How much more a REVEAL exposure counts than an in-round one (§4.5: "weight reveal exposures more
 * than in-round exposures, since the reveal is what refreshes memory"). */
export const REVEAL_EXPOSURE_WEIGHT = 3;

/** A bullet's sampling weight (§4.5 fallowness): unused bullets are drawn most often, and repeated
 * reveal exposure decays fastest. Never zero -- a fully used bullet must still be reachable, or the
 * pool silently shrinks over months. */
export function RealBulletWeight(bullet: RealBulletForPool): number {
	const inRound = Math.max(0, bullet.exposureRoundCount ?? 0);
	const atReveal = Math.max(0, bullet.exposureRevealCount ?? 0);
	return 1 / (1 + inRound + (REVEAL_EXPOSURE_WEIGHT * atReveal));
}

/** Samples `count` real bullets for one round (§4.5, §16.2 P7).
 *
 * Three properties, each with a failure mode it prevents:
 *  1. FALLOOWNESS -- weighted by `RealBulletWeight`, so the user is not shown the same handful of
 *     striking bullets over and over (which would also train "these specific memories are the game").
 *  2. ONE PER DREAM FAMILY -- two bullets from the same dream in one round would let a judged mark
 *     transfer to the other, and would let the user score on recalling that dream rather than on
 *     general familiarity. Enforced on the first pass, and relaxed only when the corpus cannot supply
 *     `count` distinct families -- a round that silently comes out half-empty would be worse.
 *  3. NO MUTATION -- the caller's corpus array is never reordered (it is usually a live accessor's
 *     array). */
export function SampleRealBullets(corpus: RealBulletForPool[], count: number, rng: () => number): RealBulletForPool[] {
	const wanted = Math.max(0, Math.min(Math.round(count), corpus.length));
	if (wanted == 0) return [];

	const remaining = [...corpus];
	const usedFamilies = new Set<string>();
	const picked: RealBulletForPool[] = [];

	const drawOne = (respectFamilies: boolean): boolean=>{
		const eligible = respectFamilies ? remaining.filter(a=>!usedFamilies.has(a.familyId)) : remaining;
		if (eligible.length == 0) return false;

		const weights = eligible.map(a=>RealBulletWeight(a));
		const total = weights.reduce((sum, weight)=>sum + weight, 0);
		let threshold = rng() * total;
		let index = eligible.length - 1;
		for (let i = 0; i < eligible.length; i++) {
			threshold -= weights[i];
			if (threshold <= 0) {
				index = i;
				break;
			}
		}

		const bullet = eligible[index];
		picked.push(bullet);
		usedFamilies.add(bullet.familyId);
		remaining.splice(remaining.indexOf(bullet), 1);
		return true;
	};

	// pass 1: respect the family rule
	while (picked.length < wanted && drawOne(true)) { /* keep drawing */ }
	// pass 2: the corpus has fewer families than the round needs -- relax rather than come up short
	while (picked.length < wanted && drawOne(false)) { /* keep drawing */ }

	return picked;
}

// the pool (§16.2 P7)
// ==========

/** One bullet that survived to become a slot, before weaving. */
export type PoolSlotDraft = {
	sourceKind: "real"|"fake";
	sourceId: string;
	/** text before reshaping (the indexed real text, or the fake's generated text) */
	originalText: string;
	reshapeWords: string[];
	reshapedText: string;
	retentionOk: boolean;
};

export type BuildBulletPoolOptions = {
	/** total bullets in the round (reals + fakes) */
	N: number;
	/** share of bullets that come from the journal (§9.1); X = .65 means 35% fakes */
	X: number;
	/** the usable corpus rows (§4.4-filtered) */
	corpus: RealBulletForPool[];
	/** the filtered seed-word list (§5.2) */
	wordlist: string[];
	/** corpus-measured distributions the fakes must match (§5.3) */
	distributions: FakeGenerationDistributions;
	/** `()=>number` in `[0, 1)`; pass a seeded RNG for reproducible tests */
	rng: () => number;
	/** false = "unseeded round" (§6 mitigation): no seed words for any bullet. The bullets are still retold
	 * (2026-10-21), so the wording changes even when nothing is added. */
	seedingEnabled?: boolean;
	/** cliché phrases to avoid (§5.5) */
	blacklist?: string[]|n;
	/** the user's banned terms (§4.4) */
	bannedWords?: string[]|n;
	/** models to rotate the fakes over (§5.5); empty = the generator role's configured model */
	generatorModels?: string[]|n;
	/** model for the `reshape`/`retention` roles; "" lets the backend/settings decide */
	reshapeModel?: string|n;
	/** attempts per bullet before it is dropped. Default 3 (§16.2 P7). */
	maxAttempts?: number;
	/** per-round tally (§13.1) */
	counter?: LLMCallCounter|n;
	/** slot-id generator; defaults to `GenerateSlotId`. Injectable so a test can assert the ids are
	 * assigned AFTER the shuffle. */
	slotIdFactory?: (()=>string)|n;
	/** Optional rejection sampling for the fakes (§5.5, P10). Off by default; see `RejectionSamplingOptions`. */
	rejectionSampling?: RejectionSamplingOptions|n;
};

export type DroppedBullet = {
	sourceKind: "real"|"fake";
	sourceId: string;
	reason: string;
};

export type BulletPoolResult = {
	/** the round's slots, shuffled, with random ids. `wovenText`/`finalText` are "" until P8 weaves
	 * them; `rewriteCount`/`resampleCount` are 0 until the judge loop (§9.3) touches them. */
	slots: RoundSlot[];
	/** every fake generated for this round, including rejected candidates (§5 invariant #5: fakes live
	 * only in the round record) */
	generatedBullets: GeneratedBullet[];
	/** bullets that were dropped, with why -- a round smaller than N is visible rather than mysterious */
	dropped: DroppedBullet[];
	/** the seeded-word count drawn for each surviving bullet, real and fake, in pool order; kept so the
	 * §16.2 P7 symmetry check can be re-verified on real data */
	reshapeWordCounts: number[];
	outputTokens: number;
};

/** Builds one round's bullet pool (§16.2 P7): sample reals -> generate fakes -> reshape everything with
 * a shared word-count distribution -> §7 check everything -> shuffle -> assign random slot ids.
 *
 * A bullet dropped at any stage (a fake the generator could not produce cleanly, a reshape that keeps
 * violating §7, a real bullet whose reshape lost its event) simply leaves the round smaller. That is
 * deliberate: a labelled-but-wrong bullet would corrupt the very signal the game trains, while a
 * smaller round is just a smaller round. */
export async function BuildBulletPool(backend: LLMBackend, options: BuildBulletPoolOptions): Promise<BulletPoolResult> {
	const N = Math.max(0, Math.round(options.N));
	const X = Math.max(0, Math.min(1, options.X));
	const seedingEnabled = options.seedingEnabled ?? true;
	const maxAttempts = Math.max(1, options.maxAttempts ?? 3);
	const slotIdFactory = options.slotIdFactory ?? GenerateSlotId;

	const realCount = Math.min(N, Math.round(N * X));
	const fakeCount = Math.max(0, N - realCount);

	const dropped: DroppedBullet[] = [];
	let outputTokens = 0;

	// 1) reals
	const reals = SampleRealBullets(options.corpus, realCount, options.rng);

	// 2) fakes (§5): one independent call each, model rotated per fake (§5.5)
	const fakeResults = fakeCount == 0 ? [] : await GenerateFakes(backend, {
		wordlist: options.wordlist,
		distributions: options.distributions,
		rng: options.rng,
		count: fakeCount,
		generatorModels: options.generatorModels,
		blacklist: options.blacklist,
		bannedWords: options.bannedWords,
		counter: options.counter,
		maxAttempts,		rejectionSampling: options.rejectionSampling,	});

	const generatedBullets: GeneratedBullet[] = [];
	const fakes: GeneratedBullet[] = [];
	for (const result of fakeResults) {
		generatedBullets.push(...result.candidates);
		outputTokens += result.outputTokens;
		if (result.bullet == null) {
			for (const candidate of result.candidates) {
				dropped.push({sourceKind: "fake", sourceId: candidate.id, reason: `generator: ${candidate.rejected ?? result.lastRejectReason ?? "unknown"}`});
			}
			continue;
		}
		fakes.push(result.bullet);
	}

	// 3) reshape EVERYTHING through one list, so the two classes cannot be treated differently: the
	//    word count comes from the shared sampler, and the loop below branches on nothing but `isReal`
	//    (which selects the retention check, not the reshape itself)
	const drafts: PoolSlotDraft[] = [];
	const reshapeWordCounts: number[] = [];

	// leak references: a real is checked against its source dream's raw text, a fake against nothing extra --
	// `ReshapeBullet` always checks against the input text itself, which for a fake IS its predecessor. Same
	// check, same retry wording, for both classes.
	const items: {sourceKind: "real"|"fake", sourceId: string, text: string, isReal: boolean, leakReferences: string[]}[] = [
		...reals.map(bullet=>({sourceKind: "real" as const, sourceId: bullet.id, text: bullet.text, isReal: true, leakReferences: bullet.leakReferences ?? []})),
		...fakes.map(bullet=>({sourceKind: "fake" as const, sourceId: bullet.id, text: bullet.text, isReal: false, leakReferences: [] as string[]})),
	];

	for (const item of items) {
		const wordCount = seedingEnabled ? SampleReshapeWordCount(item.text, options.rng) : 0;
		const reshapeWords = wordCount == 0 ? [] : SampleDistinctWords(options.wordlist, wordCount, options.rng);

		if (reshapeWords.length == 0 && seedingEnabled) {
			// the word list is too small to seed from; §6 requires an unseeded round to be recorded as
			// such rather than silently shipping an unseeded bullet into a "seeded" round
			dropped.push({sourceKind: item.sourceKind, sourceId: item.sourceId, reason: "no seed words available"});
			continue;
		}

		// an unseeded round (§6 mitigation) still goes through the reshape -- with no words, it is a pure
		// retelling. Skipping it (the pre-2026-10-21 behaviour) shipped the indexed text nearly verbatim.
		const reshaped = await ReshapeBulletWithRetention(backend, item.text, {
			isReal: item.isReal,
			sourceText: item.text,
			seedWords: reshapeWords,
			leakReferences: item.leakReferences,
			bannedWords: options.bannedWords,
			blacklist: options.blacklist,
			model: options.reshapeModel,
			counter: options.counter,
			maxAttempts,
		});
		outputTokens += reshaped.outputTokens;

		if (reshaped.text == null) {
			dropped.push({
				sourceKind: item.sourceKind, sourceId: item.sourceId,
				reason: reshaped.dropReason == "retention"
					? `retention: ${reshaped.retentionReasons[0] ?? "the event changed"}`
					: reshaped.dropReason == "phraseLeak"
						? `phrase leak: still copied ${reshaped.phraseLeaks.map(a=>`"${a}"`).join(", ")}`
						: `reshape: ${reshaped.violations[0] ?? "no usable text"}`,
			});
			continue;
		}

		drafts.push({
			sourceKind: item.sourceKind, sourceId: item.sourceId,
			originalText: item.text,
			reshapeWords,
			reshapedText: reshaped.text,
			retentionOk: reshaped.retentionOk,
		});
		reshapeWordCounts.push(reshapeWords.length);
	}

	// 4) shuffle, THEN assign ids (§16.0: ids are assigned after shuffling and never derived from the
	//    source or the class, or the story's order would leak the labels)
	ShuffleInPlace(drafts, options.rng);

	const slots: RoundSlot[] = drafts.map(draft=>({
		slotId: slotIdFactory(),
		sourceKind: draft.sourceKind,
		sourceId: draft.sourceId,
		reshapeWords: draft.reshapeWords,
		reshapedText: draft.reshapedText,
		wovenText: "", // filled by the weaver (P8)
		finalText: "", // filled by the weaver (P8), then by repair rewrites (§9.3)
		retentionOk: draft.retentionOk,
		rewriteCount: 0,
		resampleCount: 0,
		wovenEditDistance: null,
	}));

	return {slots, generatedBullets, dropped, reshapeWordCounts, outputTokens};
}

// helpers
// ==========

/** Picks `count` distinct words for one bullet's reshape (§6). Delegates to the same sampler the fake
 * generator uses, so a round's seeded vocabulary is drawn from one filtered list. */
export function SampleDistinctWords(wordlist: string[], count: number, rng: () => number): string[] {
	return SampleSeedWords(wordlist, count, rng);
}

/** Generates a random slot id (§16.0 "ids must be random -- never derived from source or class").
 *
 * Local rather than `mobx-graphlink`'s `GenerateUUID`, because this module must stay importable by the
 * vitest Node lane (importing mobx-graphlink pulls @apollo/client, which does not load under Node 22 --
 * see the other Echo Hunt pure modules). Uses the platform's CSPRNG when there is one and falls back to
 * `Math.random`, which is fine here: the requirement is unpredictability of the ORDER, not of a secret.
 */
export function GenerateSlotId(): string {
	const cryptoObj = (globalThis as {crypto?: {randomUUID?: ()=>string}}).crypto;
	if (typeof cryptoObj?.randomUUID == "function") return cryptoObj.randomUUID();

	// RFC-4122-ish v4 shape, so an id still looks like one everywhere it is logged
	const hex = (length: number)=>[...Array(length)].map(()=>Math.floor(Math.random() * 16).toString(16)).join("");
	return `${hex(8)}-${hex(4)}-4${hex(3)}-${["8", "9", "a", "b"][Math.floor(Math.random() * 4)]}${hex(3)}-${hex(12)}`;
}

/** In-place Fisher-Yates. In place because the drafts carry the labels: copying would risk the caller
 * keeping an un-shuffled reference to them. */
export function ShuffleInPlace<T>(items: T[], rng: () => number): T[] {
	for (let i = items.length - 1; i > 0; i--) {
		const j = Math.min(i, Math.max(0, Math.floor(rng() * (i + 1))));
		const temp = items[i];
		items[i] = items[j];
		items[j] = temp;
	}
	return items;
}

/** How many real bullets a round of `N` at real-share `X` should contain. Exported so the UI and the
 * pool builder cannot disagree about the count the user is told (§10.1 "you're told how many bullets
 * are dream-sourced"). */
export function RealBulletCountForRound(N: number, X: number): number {
	return Math.min(Math.max(0, Math.round(N)), Math.round(Math.max(0, Math.round(N)) * Math.max(0, Math.min(1, X))));
}

/** The label-free view model the hunt UI may receive before the reveal (§16.0 "label hygiene"): slot id
 * and the text the user reads, and NOTHING else. `sourceKind`/`sourceId` are deliberately absent, so a
 * component that only takes this type cannot leak the labels even by accident. */
export type HuntSlotView = {
	slotId: string;
	text: string;
};

export function ToHuntSlotView(slots: RoundSlot[]|n): HuntSlotView[] {
	return (slots ?? []).map(slot=>({slotId: slot.slotId, text: slot.finalText.length > 0 ? slot.finalText : slot.wovenText.length > 0 ? slot.wovenText : slot.reshapedText}));
}
